import { useState } from 'react';
import { Download, FileSpreadsheet, X } from 'lucide-react';
// Using ExcelJS for full style support
import * as XLSX from 'xlsx';
import { unzipSync, zipSync } from 'fflate';
import ExcelJS from 'exceljs';
import { Button } from '../ui/button.tsx';
import {
  getCOATemplates,
  getCOATemplateById,
  getReleaseCompareRowsByBatch,
  type COATemplateDto,
  type COATemplateItemDto,
  type CompareRowDto,
} from '../../../services/coaReleaseService.ts';

interface ExportComparisonResult {
  parameter: string;
  testMethod: string;
  uom: string;
  testValueRaw: string;
}

interface ReleaseTaskExportProps {
  batchNumber: string;
  comparisonResults: ExportComparisonResult[];
  disabled?: boolean;
}

interface PreviewCell {
  key: string;
  value: string;
  colSpan: number;
  className: string;
}

interface PreviewRow {
  key: string;
  cells: PreviewCell[];
}

interface BuiltCoAExport {
  workbook: XLSX.WorkBook;
  xlsxBytes: Uint8Array;
}

const COA_TEMPLATE_SHEET_NAME = 'CoA Template';
const DATA_START_ROW_INDEX = 8;
const SPECIAL_DATA_START_ROW_INDEX = 10; // 9 header rows + 1 blank separator row
const EXPORT_RANGE = 'A1:F96'; // max range (6 columns for special template)
// XML namespace constants removed (not used for programmatic generation)
// Package/relationship XML constants removed (not used for programmatic generation)
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const TITLE_FONT_SIZE = 12;
const CLASSIFICATION_FONT_SIZE = 12;
const CLASSIFICATION_FILL_COLOR = 'FF193767';
const EXPORT_COLUMN_WIDTHS = [60, 50, 50, 60] as const;
const EXPORT_COLUMN_WIDTHS_SPECIAL = [42, 22, 30, 40, 40, 18] as const; // 6-column special template

declare const process: { env: { REACT_APP_API_URL?: string } };
const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

const cleanText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  return text.toUpperCase() === '#VALUE!' ? '' : text;
};

const formatDateOnly = (value: unknown): string => {
  const text = cleanText(value);
  if (!text) return '';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toISOString().slice(0, 10).replace(/-/g, '/');
};

const normalizeKey = (value: unknown): string =>
  cleanText(value).toLowerCase().replace(/\s+/g, '').replace(/[^\p{L}\p{N}%]+/gu, '');

const buildFullMatchKey = (parameter: unknown, testMethod: unknown, uom: unknown) =>
  `${normalizeKey(parameter)}|${normalizeKey(testMethod)}|${normalizeKey(uom)}`;

const buildMethodMatchKey = (parameter: unknown, testMethod: unknown) =>
  `${normalizeKey(parameter)}|${normalizeKey(testMethod)}`;

const getTemplateItems = (template: COATemplateDto | null): COATemplateItemDto[] =>
  Array.isArray(template?.items) ? template.items : [];

const getCellDisplayText = (sheet: XLSX.WorkSheet, rowIndex: number, columnIndex: number): string => {
  const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })] as XLSX.CellObject | undefined;
  if (!cell) return '';
  if (typeof cell.w === 'string' && cell.w.trim()) return cell.w.trim();
  return cleanText(cell.v);
};

const appendLabelValue = (label: string, value: unknown) => {
  const text = cleanText(value);
  return text ? `${label} ${text}` : label;
};

const hasMeaningfulItemContent = (item: COATemplateItemDto) =>
  [item.classification, item.parameter, item.uom, item.testMethod]
    .some((value) => cleanText(value).length > 0);

// Helper function removed - merges now handled directly in buildCoATemplateWorkbook

const buildTestResultLookup = (compareRows: CompareRowDto[], fallbackRows: ExportComparisonResult[]) => {
  const full = new Map<string, string>();
  const method = new Map<string, string>();
  const parameter = new Map<string, string>();

  const add = (parameterValue: unknown, testMethodValue: unknown, uomValue: unknown, testResultValue: unknown) => {
    const value = cleanText(testResultValue);
    const fullKey = buildFullMatchKey(parameterValue, testMethodValue, uomValue);
    const methodKey = buildMethodMatchKey(parameterValue, testMethodValue);
    const parameterKey = normalizeKey(parameterValue);

    if (fullKey !== '||' && !full.has(fullKey)) full.set(fullKey, value);
    if (methodKey !== '|' && !method.has(methodKey)) method.set(methodKey, value);
    if (parameterKey && !parameter.has(parameterKey)) parameter.set(parameterKey, value);
  };

  compareRows.forEach((item) => add(item.parameter, item.testMethod, item.uom, item.testValue));
  fallbackRows.forEach((item) => add(item.parameter, item.testMethod, item.uom, item.testValueRaw));

  return { full, method, parameter };
};

const getTestResultValue = (
  item: COATemplateItemDto,
  lookup: ReturnType<typeof buildTestResultLookup>
) => {
  const fullKey = buildFullMatchKey(item.parameter, item.testMethod, item.uom);
  const methodKey = buildMethodMatchKey(item.parameter, item.testMethod);
  const parameterKey = normalizeKey(item.parameter);

  return (
    lookup.full.get(fullKey) ??
    lookup.method.get(methodKey) ??
    lookup.parameter.get(parameterKey) ??
    ''
  );
};

const isCoATableHeaderRow = (sheet: XLSX.WorkSheet, rowIndex: number): boolean => {
  const uom = normalizeKey(getCellDisplayText(sheet, rowIndex, 1));
  const testResult = normalizeKey(getCellDisplayText(sheet, rowIndex, 2));
  const testMethod = normalizeKey(getCellDisplayText(sheet, rowIndex, 3));

  return uom.includes('uom') && testResult.includes('testresult') && testMethod.includes('testmethod');
};

const isCoAClassificationRow = (sheet: XLSX.WorkSheet, rowIndex: number): boolean => {
  if (rowIndex < DATA_START_ROW_INDEX) return false;

  const classification = cleanText(getCellDisplayText(sheet, rowIndex, 0));
  const uom = normalizeKey(getCellDisplayText(sheet, rowIndex, 1));
  const testResult = normalizeKey(getCellDisplayText(sheet, rowIndex, 2));

  return Boolean(classification) && uom.includes('uom') && testResult.includes('testresult');
};

const isCoAClassificationRowFromUpdates = (updates: Record<string, string>, rowIndex: number): boolean => {
  if (rowIndex < DATA_START_ROW_INDEX) return false;

  const classification = cleanText(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })]);
  const uom = normalizeKey(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 1 })]);
  const testResult = normalizeKey(updates[XLSX.utils.encode_cell({ r: rowIndex, c: 2 })]);
  return Boolean(classification) && uom.includes('uom') && testResult.includes('testresult');
};

const setCellUpdate = (updates: Record<string, string>, address: string, value: unknown) => {
  updates[address] = cleanText(value);
};
// The main synchronization function that builds the cell updates based on the template and comparison data
const syncTemplateMasterRows = (updates: Record<string, string>, template: COATemplateDto) => {
  setCellUpdate(updates, 'A1', 'CERTIFICATE OF ANALYSIS');
  setCellUpdate(updates, 'A3', 'Non-Initial Shipment');

  setCellUpdate(updates, 'A5', 'Product Description:');
  setCellUpdate(updates, 'B5', template.productDescription);
  setCellUpdate(updates, 'C5', 'Manufacture Date:');
  setCellUpdate(updates, 'D5', formatDateOnly(template.manufactureDate));

  setCellUpdate(updates, 'A6', 'Spec Number:');
  setCellUpdate(updates, 'B6', template.specNumber);
  setCellUpdate(updates, 'C6', 'Expiry Date:');
  setCellUpdate(updates, 'D6', formatDateOnly(template.expiryDate));

  setCellUpdate(updates, 'A7', 'Batch Number:');
  setCellUpdate(updates, 'B7', template.batchNumber);
};

const isSpecialTemplate = (template: COATemplateDto): boolean =>
  Boolean(template.manufacturer || template.productType || template.countryOfOrigin ||
          template.packaging || template.testingPurpose || template.client);

//预览显示
const syncTemplateMasterRowsSpecial = (updates: Record<string, string>, template: COATemplateDto) => {
  // Row 1: title (A1:F1) — include report number on second line inside the merged title cell when present
  //, template.reportNumber  \nReport Number 报告编号：${template.reportNumber}
  setCellUpdate(updates, 'A1', 'CERTIFICATE OF ANALYSIS');

  // Row 2: subtitle
  setCellUpdate(updates, 'A2', 'Non-Initial Shipment');

  // Rows 3–9: bilingual label/value pairs
  setCellUpdate(updates, 'A3', 'Product Name 样品名称');
  setCellUpdate(updates, 'B3', template.productDescription);

  setCellUpdate(updates, 'A4', 'Manufacturer 生产商');
  setCellUpdate(updates, 'B4', template.manufacturer);

  // Rows 5–9: A=label1 (dark blue), B:C=value1 (merged), D=label2 (dark blue), E:F=value2 (merged)
  setCellUpdate(updates, 'A5', 'Product Type 产品类型');
  setCellUpdate(updates, 'B5', template.productType);
  setCellUpdate(updates, 'D5', 'Country of Origin 原产国');
  setCellUpdate(updates, 'E5', template.countryOfOrigin);

  setCellUpdate(updates, 'A6', 'Received Date 样品受理日期');
  setCellUpdate(updates, 'B6', formatDateOnly(template.sampleReceivedDate ?? undefined));
  setCellUpdate(updates, 'D6', 'Packaging 包装规格');
  setCellUpdate(updates, 'E6', template.packaging);

  setCellUpdate(updates, 'A7', 'Manufacture Date 生产日期');
  setCellUpdate(updates, 'B7', formatDateOnly(template.manufactureDate));
  setCellUpdate(updates, 'D7', 'Expiry Date 保质期');
  setCellUpdate(updates, 'E7', formatDateOnly(template.expiryDate));

  setCellUpdate(updates, 'A8', 'Testing Purpose 检测目的');
  setCellUpdate(updates, 'B8', template.testingPurpose);
  setCellUpdate(updates, 'D8', 'Client 委托单位（人）');
  setCellUpdate(updates, 'E8', template.client);

  setCellUpdate(updates, 'A9', 'Product Code 产品编号');
  setCellUpdate(updates, 'B9', template.sampleSubmissionRefNumber);
  setCellUpdate(updates, 'D9', 'Batch Number 产品批次');
  setCellUpdate(updates, 'E9', template.batchNumber);
};

const syncTemplateDetailRows = (
  sheet: XLSX.WorkSheet,
  updates: Record<string, string>,
  template: COATemplateDto,
  compareRows: CompareRowDto[],
  fallbackRows: ExportComparisonResult[],
  footerStartRow: number,
  dataStartIndex: number = DATA_START_ROW_INDEX,
  isSpecial: boolean = false
) => {
  const lookup = buildTestResultLookup(compareRows, fallbackRows);
  const templateItems = getTemplateItems(template).filter(hasMeaningfulItemContent);

  type ItemGroup = {
    label: string;
    items: COATemplateItemDto[];
  };

  const groupedItems = new Map<string, ItemGroup>();
  const itemGroupsInOrder: ItemGroup[] = [];

  templateItems.forEach((item) => {
    const rawClassification = cleanText(item.classification) || 'General';
    let group = groupedItems.get(rawClassification);

    if (!group) {
      group = { label: rawClassification, items: [] };
      groupedItems.set(rawClassification, group);
      itemGroupsInOrder.push(group);
    }

    group.items.push(item);
  });

  const detailRowIndexes: number[] = [];
  for (let rowIndex = dataStartIndex; rowIndex < footerStartRow; rowIndex++) {
    detailRowIndexes.push(rowIndex);
  }

  const writeCategoryHeaderRow = (rowIndex: number, classificationLabel: string) => {
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 0 }), classificationLabel);
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 1 }), 'UOM 单位');
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 2 }), 'Test Result 检测结果');
    if (isSpecial) {
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 3 }), 'Standard Value 标准值');
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 4 }), 'Test Method 检测方法');
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 5 }), 'Result 结论');
    } else {
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 3 }), 'Test Method 检测方法');
    }
  };

  const writeItemRow = (rowIndex: number, item: COATemplateItemDto) => {
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 0 }), item.parameter);
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 1 }), item.uom);
    setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 2 }), getTestResultValue(item, lookup));
    if (isSpecial) {
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 3 }), item.standardValue);
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 4 }), item.testMethod);
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 5 }), item.result);
    } else {
      setCellUpdate(updates, XLSX.utils.encode_cell({ r: rowIndex, c: 3 }), item.testMethod);
    }
  };

  let rowPointer = 0;

  for (const group of itemGroupsInOrder) {
    if (!group.items.length) continue;

    if (rowPointer >= detailRowIndexes.length) break;

    const categoryHeaderRowIndex = detailRowIndexes[rowPointer++];
    writeCategoryHeaderRow(categoryHeaderRowIndex, group.label);

    for (const item of group.items) {
      if (rowPointer >= detailRowIndexes.length) break;
      const dataRowIndex = detailRowIndexes[rowPointer++];
      writeItemRow(dataRowIndex, item);
    }
  }
};

const syncTemplateFooterRows = (updates: Record<string, string>, template: COATemplateDto, footerStartRow: number) => {
  const descriptionLines = cleanText(template.description)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const descriptionRows = descriptionLines.length > 5
    ? [...descriptionLines.slice(0, 4), descriptionLines.slice(4).join('\n')]
    : descriptionLines;

  for (let offset = 0; offset < 5; offset++) {
    const rowNumber = footerStartRow + offset + 1;
    setCellUpdate(updates, `A${rowNumber}`, descriptionRows[offset] || '');
  }

  const metadataStartRow = footerStartRow + 7;
  setCellUpdate(updates, `A${metadataStartRow}`, appendLabelValue('Prepared by:', template.preparedBy));
  setCellUpdate(updates, `C${metadataStartRow}`, appendLabelValue('Released by:', template.releaseBy));
  setCellUpdate(updates, `A${metadataStartRow + 1}`, appendLabelValue('Title:', template.preparedByTitle));
  setCellUpdate(updates, `C${metadataStartRow + 1}`, appendLabelValue('Title:', template.releaseByTitle));
  setCellUpdate(updates, `A${metadataStartRow + 2}`, appendLabelValue('Signature:', template.preparedBySignature));
  setCellUpdate(updates, `C${metadataStartRow + 2}`, appendLabelValue('Signature:', template.releaseBySignature));
  setCellUpdate(updates, `A${metadataStartRow + 3}`, '');
  setCellUpdate(updates, `C${metadataStartRow + 3}`, '');
  setCellUpdate(updates, `A${metadataStartRow + 5}`, appendLabelValue('Date:', formatDateOnly(template.preparedByDate)));
  setCellUpdate(updates, `C${metadataStartRow + 5}`, appendLabelValue('Date:', formatDateOnly(template.releaseByDate)));
};

const syncDisplayedFormulaValues = (sheet: XLSX.WorkSheet, updates: Record<string, string>) => {
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);

  for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
    for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = sheet[address] as XLSX.CellObject | undefined;
      if (!cell?.f || Object.prototype.hasOwnProperty.call(updates, address)) continue;

      setCellUpdate(updates, address, getCellDisplayText(sheet, rowIndex, columnIndex));
    }
  }
};

const buildTemplateCellUpdates = (
  sheet: XLSX.WorkSheet,
  template: COATemplateDto,
  compareRows: CompareRowDto[],
  fallbackRows: ExportComparisonResult[],
  footerStartRow: number,
  isSpecial: boolean = false
) => {
  const updates: Record<string, string> = {};
  const dataStartIndex = isSpecial ? SPECIAL_DATA_START_ROW_INDEX : DATA_START_ROW_INDEX;

  if (isSpecial) {
    syncTemplateMasterRowsSpecial(updates, template);
  } else {
    syncTemplateMasterRows(updates, template);
  }
  syncTemplateDetailRows(sheet, updates, template, compareRows, fallbackRows, footerStartRow, dataStartIndex, isSpecial);
  syncTemplateFooterRows(updates, template, footerStartRow);
  syncDisplayedFormulaValues(sheet, updates);

  return updates;
};

const getDynamicExportRowCount = (template: COATemplateDto) => {
  const itemCount = getTemplateItems(template).filter(hasMeaningfulItemContent).length;
  const groupCount = new Set(
    getTemplateItems(template)
      .filter(hasMeaningfulItemContent)
      .map((item) => cleanText(item.classification) || 'General')
  ).size;
  const detailRows = itemCount + groupCount;
  const footerRows = 12;
  const minimumRows = 10;
  const headerRows = isSpecialTemplate(template) ? SPECIAL_DATA_START_ROW_INDEX : DATA_START_ROW_INDEX;
  return Math.max(minimumRows, headerRows + detailRows + footerRows);
};

// getTargetSheetName removed — not needed for programmatic generation

// Default VML used when a template has headerImagePath but no extracted headerVmlXml.
// Places the image in the left header section (shape id 1025 = LH).
const DEFAULT_HEADER_VML_XML = '<xml xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><o:shapelayout v:ext="edit"><o:idmap v:ext="edit" data="1"/></o:shapelayout><v:shapetype id="_x0000_t75" coordsize="21600,21600" o:spt="75" o:preferrelative="t" path="m@4@5l@4@11@9@11@9@5xe" filled="f" stroked="f"><v:stroke joinstyle="miter"/><v:formulas><v:f eqn="if lineDrawn pixelLineWidth 0"/><v:f eqn="sum @0 1 0"/><v:f eqn="sum 0 0 @1"/><v:f eqn="prod @2 1 2"/><v:f eqn="prod @3 4669 1001"/><v:f eqn="prod @3 6858 1001"/><v:f eqn="prod @3 23929 1001"/><v:f eqn="prod @3 1 2"/><v:f eqn="prod @3 2 1"/><v:f eqn="sum @7 0 1"/><v:f eqn="prod @3 3 4"/><v:f eqn="sum @0 0 1"/></v:formulas><v:path o:extrusionok="f" gradientshapeok="t" o:connecttype="rect"/><o:lock v:ext="edit" aspectratio="t"/></v:shapetype><v:shape id="_x0000_s1025" type="#_x0000_t75" style="position:absolute;margin-left:0;margin-top:0;width:500pt;height:72pt;z-index:1" filled="f" stroked="f"><v:imagedata o:relid="rId1" o:title=""/><o:lock v:ext="edit" rotation="t"/></v:shape></xml>';


const injectPageMarginsIntoXlsx = (
  xlsxBytes: Uint8Array,
  margins: { left: number; right: number; top: number; bottom: number; header: number; footer: number }
): Uint8Array => {
  const files = unzipSync(xlsxBytes) as Record<string, Uint8Array>;
  const wsKey = Object.keys(files).find((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k));
  if (!wsKey) return xlsxBytes;

  const fmt = (n: number) => parseFloat(n.toFixed(4)).toString();
  const tag = `<pageMargins left="${fmt(margins.left)}" right="${fmt(margins.right)}" top="${fmt(margins.top)}" bottom="${fmt(margins.bottom)}" header="${fmt(margins.header)}" footer="${fmt(margins.footer)}"/>`;
  let wsXml = new TextDecoder().decode(files[wsKey]);

  const openIdx = wsXml.indexOf('<pageMargins');
  if (openIdx !== -1) {
    // Replace the existing <pageMargins> element in-place
    const closeGT = wsXml.indexOf('>', openIdx);
    if (closeGT !== -1) {
      if (wsXml[closeGT - 1] === '/') {
        // Self-closing: <pageMargins ... />
        wsXml = wsXml.slice(0, openIdx) + tag + wsXml.slice(closeGT + 1);
      } else {
        // Regular closing tag: <pageMargins ...>...</pageMargins>
        const closeTag = '</pageMargins>';
        const closeIdx = wsXml.indexOf(closeTag, closeGT);
        wsXml = wsXml.slice(0, openIdx) + tag + wsXml.slice(closeIdx !== -1 ? closeIdx + closeTag.length : closeGT + 1);
      }
    }
  } else {
    // ExcelJS omits <pageMargins> when using defaults.
    // Per OOXML schema, <pageMargins> must appear BEFORE <pageSetup>.
    const pageSetupIdx = wsXml.indexOf('<pageSetup');
    if (pageSetupIdx !== -1) {
      wsXml = wsXml.slice(0, pageSetupIdx) + tag + wsXml.slice(pageSetupIdx);
    } else {
      wsXml = wsXml.replace('</worksheet>', `${tag}</worksheet>`);
    }
  }

  files[wsKey] = new TextEncoder().encode(wsXml);
  return zipSync(files as any) as unknown as Uint8Array<ArrayBuffer>;
};

const injectHeaderImageIntoXlsx = (
  xlsxBytes: Uint8Array,
  vmlXml: string,
  imageData: Uint8Array,
  imageExt: string
): Uint8Array => {
  const files = unzipSync(xlsxBytes) as Record<string, Uint8Array>;

  const wsKey = Object.keys(files).find((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k));
  if (!wsKey) return xlsxBytes;

  const wsFileName = wsKey.split('/').pop()!;
  const wsRelsKey = `xl/worksheets/_rels/${wsFileName}.rels`;
  const vmlKey = 'xl/drawings/vmlDrawingHF.vml';
  const vmlRelsKey = 'xl/drawings/_rels/vmlDrawingHF.vml.rels';
  const imageKey = `xl/media/hf_image.${imageExt}`;
  const VML_REL_ID = 'rId_hfVml1';
  const VML_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/vmlDrawing';
  const IMG_REL_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/image';
  const RELS_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

  // Inject <legacyDrawingHF> into worksheet XML
  let wsXml = new TextDecoder().decode(files[wsKey]);
  if (!wsXml.includes('xmlns:r=')) {
    wsXml = wsXml.replace(
      '<worksheet ',
      '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" '
    );
  }
  if (!wsXml.includes('<legacyDrawingHF')) {
    wsXml = wsXml.replace('</worksheet>', `<legacyDrawingHF r:id="${VML_REL_ID}"/></worksheet>`);
  }
  files[wsKey] = new TextEncoder().encode(wsXml);

  // Add VML relationship to sheet .rels
  const newRel = `<Relationship Id="${VML_REL_ID}" Type="${VML_REL_TYPE}" Target="../drawings/vmlDrawingHF.vml"/>`;
  if (files[wsRelsKey]) {
    const existing = new TextDecoder().decode(files[wsRelsKey]);
    files[wsRelsKey] = new TextEncoder().encode(
      existing.replace('</Relationships>', `${newRel}</Relationships>`)
    );
  } else {
    files[wsRelsKey] = new TextEncoder().encode(
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n<Relationships xmlns="${RELS_NS}">${newRel}</Relationships>`
    );
  }

  // Add VML drawing file
  files[vmlKey] = new TextEncoder().encode(vmlXml);

  // Extract o:relid from VML and create VML .rels pointing to the image
  const relIdMatch = vmlXml.match(/o:relid="([^"]+)"/);
  const imgRelId = relIdMatch ? relIdMatch[1] : 'rId1';
  files[vmlRelsKey] = new TextEncoder().encode(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n<Relationships xmlns="${RELS_NS}"><Relationship Id="${imgRelId}" Type="${IMG_REL_TYPE}" Target="../media/hf_image.${imageExt}"/></Relationships>`
  );

  // Add image bytes
  files[imageKey] = imageData;

  // Register new parts in [Content_Types].xml
  const ctKey = '[Content_Types].xml';
  if (files[ctKey]) {
    let ctXml = new TextDecoder().decode(files[ctKey]);
    if (!ctXml.includes('Extension="vml"')) {
      ctXml = ctXml.replace(
        '</Types>',
        `<Default Extension="vml" ContentType="application/vnd.openxmlformats-officedocument.vmlDrawing"/></Types>`
      );
    }
    const extLower = imageExt.toLowerCase();
    const imgMime: Record<string, string> = {
      png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
      gif: 'image/gif', bmp: 'image/bmp',
    };
    if (!ctXml.toLowerCase().includes(`extension="${extLower}"`)) {
      ctXml = ctXml.replace(
        '</Types>',
        `<Default Extension="${extLower}" ContentType="${imgMime[extLower] || 'image/png'}"/></Types>`
      );
    }
    files[ctKey] = new TextEncoder().encode(ctXml);
  }

  return zipSync(files as any) as unknown as Uint8Array<ArrayBuffer>;
};

const buildCoATemplateWorkbook = async (
  template: COATemplateDto,
  compareRows: CompareRowDto[],
  fallbackRows: ExportComparisonResult[],
  headerImage: { data: Uint8Array; ext: string } | null = null
): Promise<BuiltCoAExport> => {
  // Build data structure
  const isSpecial = isSpecialTemplate(template);
  const dataStartIndex = isSpecial ? SPECIAL_DATA_START_ROW_INDEX : DATA_START_ROW_INDEX;
  const totalRows = getDynamicExportRowCount(template);
  const footerStartRow = Math.max(dataStartIndex + 1, totalRows - 12);

  const updates = buildTemplateCellUpdates(
    {} as XLSX.WorkSheet,
    template,
    compareRows,
    fallbackRows,
    footerStartRow,
    isSpecial
  );

  const colCount = isSpecial ? 6 : 4;
  const lastCol = colCount === 6 ? 'F' : 'D';
  const colWidths = isSpecial ? EXPORT_COLUMN_WIDTHS_SPECIAL : EXPORT_COLUMN_WIDTHS;

  // Create ExcelJS workbook with styles
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(COA_TEMPLATE_SHEET_NAME);

  // Set column widths
  worksheet.columns = Array.from({ length: colCount }, (_, i) => ({ width: colWidths[i] }));

  const effectivePageHeader = template.pageHeader || (template.headerImagePath ? '&L&G' : null);
  if (effectivePageHeader || template.pageFooter) {
    (worksheet as any).headerFooter = {
      oddHeader: effectivePageHeader || '',
      oddFooter: template.pageFooter || '',
    };
  }

  worksheet.views = [
    {
      state: 'normal',
      zoomScale: isSpecial ? 75 : 90,
      showGridLines: false,
    },
  ];

  worksheet.pageSetup = {
    //设置字体为 等线(正文)
    orientation: 'portrait', //横向展示，纵向展示为'portrait'
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0, // 0 = allow multiple pages vertically
    //A4 is 9, A3 is 8 in ExcelJS, but since A3 isn't in the enum we can just use the OOXML value directly
    paperSize: 9 as any, // A4 — not in ExcelJS enum but valid OOXML value
    horizontalDpi: 300,
    verticalDpi: 300,
    showGridLines: false,
    printArea: `A1:${lastCol}${totalRows}`,
  };

  // Default margins match the required print layout (in inches; 1 cm = 1/2.54 in):
  // top=3cm, bottom=1.9cm, left=0.6cm, right=0.6cm, header=0.8cm, footer=0.8cm
  const defaultMargins = {
    left:   0.24, // 0.6 cm
    right:  0.24, // 0.6 cm
    top:    1.18, // 3 cm
    bottom: 0.75, // 1.9 cm
    header: 0.31, // 0.8 cm
    footer: 0.31, // 0.8 cm
  };
  let parsedMargins = defaultMargins;
  if (template.pageMargins) {
    try {
      const stored = JSON.parse(template.pageMargins) as Record<string, number>;
      parsedMargins = {
        left:   stored.left   ?? defaultMargins.left,
        right:  stored.right  ?? defaultMargins.right,
        top:    stored.top    ?? defaultMargins.top,
        bottom: stored.bottom ?? defaultMargins.bottom,
        header: stored.header ?? defaultMargins.header,
        footer: stored.footer ?? defaultMargins.footer,
      };
    } catch { /* keep default */ }
  }
  // pageMargins are injected directly into the worksheet XML after buffer generation

  // Initialize all rows with data
  for (let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
    const row: (string | null)[] = [];
    for (let colIndex = 1; colIndex <= colCount; colIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex - 1, c: colIndex - 1 });
      row.push(updates[address] || '');
    }
    worksheet.addRow(row);
  }

  // Apply styles and formatting
  for (let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
    const excelRow = worksheet.getRow(rowIndex);
    const rowIdx0Based = rowIndex - 1;

    // Helper: Check if this is a header row
    const isHeaderRow = () => {
      const colB = cleanText(updates[XLSX.utils.encode_cell({ r: rowIdx0Based, c: 1 })] || '').toLowerCase();
      const colC = cleanText(updates[XLSX.utils.encode_cell({ r: rowIdx0Based, c: 2 })] || '').toLowerCase();
      const colD = cleanText(updates[XLSX.utils.encode_cell({ r: rowIdx0Based, c: 3 })] || '').toLowerCase();
      return colB.includes('uom') && colC.includes('testresult') && colD.includes('testmethod');
    };

    const commonBorder = {
      top: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
      bottom: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
      left: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
      right: { style: 'thin' as const, color: { argb: 'FFCBD5E0' } },
    };

    for (let colIndex = 1; colIndex <= colCount; colIndex++) {
      const cell = excelRow.getCell(colIndex);

      const subtitleRowIdx = isSpecial ? 1 : 2;
      // Title row, subtitle row, special template product info rows, classification rows, header rows, and data rows all have specific styling rules
      if (rowIdx0Based === 0) {
        // Title row — no border on any cell
        if (isSpecial && colIndex === colCount) {
          // Last col: report number — dark bg, red text, right-aligned
          cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: CLASSIFICATION_FILL_COLOR } };
          cell.font = { name: '等线', color: { argb: 'FFFF0000' }, bold: true, size: CLASSIFICATION_FONT_SIZE };
          cell.alignment = { horizontal: 'right' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = undefined as any;
        } else {
          cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: CLASSIFICATION_FILL_COLOR } };
          cell.font = { name: '等线', color: { argb: 'FFFFFFFF' }, bold: true, size: TITLE_FONT_SIZE };
          cell.alignment = { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = undefined as any;
        }
      } else if (rowIdx0Based === subtitleRowIdx) {
        // Subtitle row: light gray background, no border
        cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF3F4F6' } };
        cell.font = { name: '等线', color: { argb: 'FF0F172A' }, bold: true, size: 12 };
        cell.alignment = { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = undefined as any;
      } else if (isSpecial && rowIdx0Based >= 2 && rowIdx0Based <= 8) {
        // Special template product info rows (rows 3–9): label cols dark blue, value cols white, no border
        const isSingleValueRow = rowIdx0Based <= 3; // Product Name & Manufacturer: A=label, B–F=value
        const isLabelCol = colIndex === 1 || (!isSingleValueRow && colIndex === 4);
        if (isLabelCol) {
          cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: CLASSIFICATION_FILL_COLOR } };
          cell.font = { name: '等线', color: { argb: 'FFFFFFFF' }, bold: true, size: 12 };
          cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = undefined as any;
        } else {
          cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFFFFFF' } };
          cell.font = { name: '等线', color: { argb: 'FF0F172A' }, size: 12 };
          cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = undefined as any;
        }
      } else if (rowIdx0Based >= dataStartIndex && rowIdx0Based < footerStartRow) {
        // Item table rows
        if (isCoAClassificationRowFromUpdates(updates, rowIdx0Based)) {
          // Classification row: same dark styling as the title
          cell.fill = {
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb: CLASSIFICATION_FILL_COLOR },
          };
          cell.font = { name: '等线', color: { argb: 'FFFFFFFF' }, bold: true, size: CLASSIFICATION_FONT_SIZE };
          cell.alignment = { horizontal: 'center' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = commonBorder as any;
        } else if (isHeaderRow()) {
          // Header row: dark background, white text
          cell.fill = {
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb: 'FF374151' },
          };
          cell.font = { name: '等线', color: { argb: 'FFFFFFFF' }, bold: true, size: 12 };
          cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
          cell.border = commonBorder as any;
        } else {
          // Data rows
          cell.fill = {
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb: 'FFFFFFFF' },
          };
          cell.font = { name: '等线', color: { argb: 'FF0F172A' }, size: 12 };
          cell.alignment = { horizontal: 'left' as const, vertical: 'top' as const, wrapText: true };
          cell.border = commonBorder as any;
        }
      } else if (rowIdx0Based >= footerStartRow && rowIdx0Based <= footerStartRow + 4) {
        // Description rows
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFFFFFFF' },
        };
        cell.font = { name: '等线', color: { argb: 'FF0F172A' }, size: 12 };
        cell.alignment = { horizontal: 'left' as const, vertical: 'top' as const, wrapText: true };
        cell.border = undefined as any;
      } else if (rowIdx0Based >= footerStartRow + 6) {
        // Metadata rows
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFFFFFFF' },
        };
        cell.font = { name: '等线', color: { argb: 'FF0F172A' }, bold: true, size: 12 };
        cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = undefined as any;
      } else {
        // Master rows (5-7)
        cell.fill = {
          type: 'pattern' as const,
          pattern: 'solid' as const,
          fgColor: { argb: 'FFFFFFFF' },
        };
        cell.font = { name: '等线', color: { argb: 'FF0F172A' }, size: 12 };
        cell.alignment = { horizontal: 'left' as const, vertical: 'middle' as const, wrapText: true };
        cell.border = undefined as any;
      }
    }
  }

  // Apply merges
  if (isSpecial) {
    worksheet.mergeCells(`A1:${lastCol}1`); // title cols full width (A1:F1)
    worksheet.mergeCells(`A2:${lastCol}2`); // subtitle — full width
    worksheet.mergeCells(`B3:${lastCol}3`);        // Product Name value
    worksheet.mergeCells(`B4:${lastCol}4`);        // Manufacturer value
    // Rows 5–9: A=label1, B:C=value1, D=label2, E:F=value2
    for (let r = 5; r <= 9; r++) {
      worksheet.mergeCells(`B${r}:C${r}`);
      worksheet.mergeCells(`E${r}:F${r}`);
    }
  } else {
    worksheet.mergeCells(`A1:D1`);
    worksheet.mergeCells(`A3:D3`);
  }
  for (let rowIndex = footerStartRow + 1; rowIndex <= footerStartRow + 5; rowIndex++) {
    worksheet.mergeCells(`A${rowIndex}:${lastCol}${rowIndex}`);
  }

  // Hide all columns beyond the used range so the download matches the preview
  for (let i = colCount + 1; i <= 26; i++) {
    worksheet.getColumn(i).hidden = true;
  }

  // Export to buffer
  const excelBuffer = await workbook.xlsx.writeBuffer();
  let xlsxBytes = new Uint8Array(excelBuffer as ArrayBuffer);
  // Inject page margins directly into worksheet XML (ExcelJS doesn't expose this via its public API)
  try {
    xlsxBytes = injectPageMarginsIntoXlsx(xlsxBytes, parsedMargins) as unknown as Uint8Array<ArrayBuffer>;
  } catch (marginsErr) {
    console.warn('Page margins injection failed:', marginsErr);
  }
  if (headerImage) {
    const effectiveVmlXml = template.headerVmlXml || DEFAULT_HEADER_VML_XML;
    try {
      const ext = (template.headerImagePath?.split('.').pop() || headerImage.ext).toLowerCase();
      xlsxBytes = injectHeaderImageIntoXlsx(xlsxBytes, effectiveVmlXml, headerImage.data, ext) as unknown as Uint8Array<ArrayBuffer>;
    } catch (vmlErr) {
      console.warn('VML header image injection failed:', vmlErr);
    }
  }

  // Also create XLSX workbook for preview rendering
  const previewWorkbook = XLSX.utils.book_new();
  const previewSheet = XLSX.utils.aoa_to_sheet(
    Array.from({ length: totalRows }, (_, r) =>
      Array.from({ length: colCount }, (_, c) => updates[XLSX.utils.encode_cell({ r, c })] || '')
    )
  );
  previewSheet['!ref'] = `A1:${lastCol}${totalRows}`;
  previewSheet['!cols'] = Array.from({ length: colCount }, (_, i) => ({ wch: colWidths[i] }));
  const baseMerges = isSpecial
    ? [
        { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }, // A1:F1 title
        { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }, // A2:F2 subtitle
        { s: { r: 2, c: 1 }, e: { r: 2, c: colCount - 1 } }, // B3:F3 product name
        { s: { r: 3, c: 1 }, e: { r: 3, c: colCount - 1 } }, // B4:F4 manufacturer
        // rows 5–9: B:C merged, E:F merged (0-indexed rows 4–8)
        ...Array.from({ length: 5 }, (_, i) => ({ s: { r: 4 + i, c: 1 }, e: { r: 4 + i, c: 2 } })),
        ...Array.from({ length: 5 }, (_, i) => ({ s: { r: 4 + i, c: 4 }, e: { r: 4 + i, c: 5 } })),
      ]
    : [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }, // A1:D1 title
        { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } }, // A3:D3 subtitle
      ];
  previewSheet['!merges'] = [
    ...baseMerges,
    ...Array.from({ length: 5 }, (_, i) => ({
      s: { r: footerStartRow + i, c: 0 },
      e: { r: footerStartRow + i, c: colCount - 1 },
    })),
  ];
  XLSX.utils.book_append_sheet(previewWorkbook, previewSheet, COA_TEMPLATE_SHEET_NAME);

  return { workbook: previewWorkbook, xlsxBytes };
};

const getMergeMap = (sheet: XLSX.WorkSheet) => {
  const starts = new Map<string, number>();
  const covered = new Set<string>();
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);

  (sheet['!merges'] || []).forEach((merge) => {
    if (merge.s.r > range.e.r || merge.s.c > range.e.c) return;
    const startKey = `${merge.s.r}:${merge.s.c}`;
    starts.set(startKey, Math.min(range.e.c, merge.e.c) - merge.s.c + 1);

    for (let rowIndex = merge.s.r; rowIndex <= merge.e.r; rowIndex++) {
      for (let columnIndex = merge.s.c; columnIndex <= Math.min(merge.e.c, range.e.c); columnIndex++) {
        if (rowIndex === merge.s.r && columnIndex === merge.s.c) continue;
        covered.add(`${rowIndex}:${columnIndex}`);
      }
    }
  });

  return { starts, covered };
};

const getPreviewCellClassName = (sheet: XLSX.WorkSheet, rowIndex: number, colSpan: number) => {
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);
  const isSpecial = range.e.c + 1 === 6;
  const footerStart = Math.max(DATA_START_ROW_INDEX + 1, range.e.r - 11);
  const tableBorder = rowIndex >= DATA_START_ROW_INDEX && rowIndex < footerStart
    ? 'border border-slate-300'
    : '';

  if (rowIndex === 0) return `${tableBorder} bg-[#193767] text-white text-center text-[14px] font-semibold`;
  if ((isSpecial && rowIndex === 1) || (!isSpecial && rowIndex === 2)) {
    return `${tableBorder} bg-slate-100 text-center font-medium text-slate-900`;
  }
  if (isCoAClassificationRow(sheet, rowIndex)) return `${tableBorder} bg-[#193767] text-white text-center text-[13px] font-semibold`;
  if (isCoATableHeaderRow(sheet, rowIndex)) return `${tableBorder} bg-slate-700 text-white font-semibold`;
  if (rowIndex >= footerStart && rowIndex <= footerStart + 4 && colSpan > 1) return `${tableBorder} bg-white text-slate-900 leading-6`;
  if (rowIndex >= footerStart + 6) return `${tableBorder} bg-white font-medium text-slate-900`;
  return `${tableBorder} bg-white text-slate-900`;
};

const buildPreviewRows = (workbook: XLSX.WorkBook): PreviewRow[] => {
  const sheet = workbook.Sheets[COA_TEMPLATE_SHEET_NAME];
  const range = XLSX.utils.decode_range(sheet['!ref'] || EXPORT_RANGE);
  const mergeMap = getMergeMap(sheet);
  const rows: PreviewRow[] = [];

  for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
    const cells: PreviewCell[] = [];

    for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex++) {
      const key = `${rowIndex}:${columnIndex}`;
      if (mergeMap.covered.has(key)) continue;

      const colSpan = mergeMap.starts.get(key) || 1;
      cells.push({
        key,
        value: getCellDisplayText(sheet, rowIndex, columnIndex),
        colSpan,
        className: getPreviewCellClassName(sheet, rowIndex, colSpan),
      });
    }

    rows.push({ key: `row-${rowIndex}`, cells });
  }

  return rows;
};

const makeSafeFilePart = (value: string) =>
  cleanText(value).replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_') || 'release_task';

export default function ReleaseTaskExport({ batchNumber, comparisonResults, disabled = false }: ReleaseTaskExportProps) {
  const [previewWorkbook, setPreviewWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<COATemplateDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');

  const loadTemplateForPreview = async () => {
    const normalizedBatch = cleanText(batchNumber);
    if (!normalizedBatch) {
      setErrorText('Batch Number is required.');
      return;
    }

    setLoading(true);
    setErrorText('');
    try {
      const templateParams = new URLSearchParams();
      templateParams.set('range', '[0,999]');
      templateParams.set('perPage', '1000');
      templateParams.set('filter', JSON.stringify({ batchNumber: normalizedBatch }));

      const [templates, compareRows] = await Promise.all([
        getCOATemplates({
          range: templateParams.get('range') || '[0,999]',
          perPage: templateParams.get('perPage') || '1000',
          filter: templateParams.get('filter') || '',
        }),
        getReleaseCompareRowsByBatch(normalizedBatch),
      ]);

      const templateMeta =
        templates.find((item) => cleanText(item.batchNumber) === normalizedBatch) ||
        null;

      if (!templateMeta) {
        throw new Error(`No COA template found for batch ${normalizedBatch}.`);
      }

      const exactTemplate = await getCOATemplateById(templateMeta.id || '');

      let headerImage: { data: Uint8Array; ext: string } | null = null;
      if (exactTemplate.headerImagePath) {
        try {
          const imgPath = exactTemplate.headerImagePath;
          let imageUrl: string;
          if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
            imageUrl = imgPath;
          } else if (imgPath.startsWith('/')) {
            // Vite serves the /public directory at the root — strip the /public prefix if present
            const normalizedPath = imgPath.startsWith('/public/') ? imgPath.slice('/public'.length) : imgPath;
            imageUrl = window.location.origin + normalizedPath;
          } else {
            const presignRes = await fetch(`${BASE_API_URL}/s3files/presign?key=${encodeURIComponent(imgPath)}&expiresMinutes=10`);
            if (!presignRes.ok) throw new Error(`Presign failed: HTTP ${presignRes.status}`);
            const presignData = await presignRes.json() as { url: string };
            imageUrl = presignData.url;
          }
          const imgRes = await fetch(imageUrl);
          if (!imgRes.ok) throw new Error(`Image fetch failed: HTTP ${imgRes.status}`);
          const imgBuffer = await imgRes.arrayBuffer();
          const ext = imgPath.split('.').pop()?.toLowerCase() || 'png';
          headerImage = { data: new Uint8Array(imgBuffer), ext };
        } catch (imgErr) {
          console.warn('Failed to fetch header image for export:', imgErr);
        }
      }

      const exportData = await buildCoATemplateWorkbook(exactTemplate, compareRows, comparisonResults, headerImage);
      setPreviewTemplate(exactTemplate);
      setPreviewWorkbook(exportData.workbook);
      setPreviewBytes(exportData.xlsxBytes);
      setPreviewRows(buildPreviewRows(exportData.workbook));
      setPreviewOpen(true);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Load export preview failed.');
      setPreviewTemplate(null);
      setPreviewWorkbook(null);
      setPreviewBytes(null);
      setPreviewRows([]);
      setPreviewOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmExport = () => {
    if (!previewBytes) return;

    // Compute SHA-256 of preview bytes for integrity check (human-verifiable)
    const computeHashHex = async (bytes: Uint8Array) => {
      const buffer = bytes.buffer instanceof ArrayBuffer ? bytes.buffer : new Uint8Array(bytes).buffer;
      const digest = await crypto.subtle.digest('SHA-256', buffer as ArrayBuffer);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    };

    const triggerDownload = (bytes: Uint8Array, hashHex?: string) => {
      const arrayBuffer = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(arrayBuffer).set(bytes);
      const blob = new Blob([arrayBuffer], { type: XLSX_MIME_TYPE });
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = `release_task_${makeSafeFilePart(batchNumber)}_coa_template.xlsx`;
      if (hashHex) {
        (link as any).dataset = { sha256: hashHex };
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    };

    void (async () => {
      try {
        const hashHex = await computeHashHex(previewBytes);
        // Log the hash so the exported file can be verified externally if needed
        console.log('Export preview SHA-256:', hashHex);

        triggerDownload(previewBytes, hashHex);
      } catch (err) {
        console.error('Failed to compute export hash', err);
        triggerDownload(previewBytes);
      }
    })();
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={disabled || loading}
        onClick={() => { void loadTemplateForPreview(); }}
      >
        <FileSpreadsheet className="size-4 mr-2" />
        {loading ? 'Loading...' : 'Export'}
      </Button>

      {errorText && (
        <span className="max-w-[260px] text-xs text-red-600">{errorText}</span>
      )}

      {previewOpen && previewWorkbook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-1.5">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Export Preview</h3>
                <p className="text-sm text-gray-500">CoA Template - Batch Number: {cleanText(batchNumber) || '-'}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPreviewOpen(false)} title="Close">
                <X className="size-3.5" />
              </Button>
            </div>

            <div className="overflow-auto p-5">
              <table className="w-full border border-slate-300 border-collapse table-fixed text-xs">
                <colgroup>
                  {previewTemplate && isSpecialTemplate(previewTemplate) ? (
                    <>
                      <col className="w-[24%]" />
                      <col className="w-[10%]" />
                      <col className="w-[15%]" />
                      <col className="w-[20%]" />
                      <col className="w-[20%]" />
                      <col className="w-[11%]" />
                    </>
                  ) : (
                    <>
                      <col className="w-[33.3%]" />
                      <col className="w-[17.6%]" />
                      <col className="w-[17.6%]" />
                      <col className="w-[31.5%]" />
                    </>
                  )}
                </colgroup>
                <tbody>
                  {previewRows.map((row) => (
                    <tr key={row.key}>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.key}
                          colSpan={cell.colSpan}
                          className={`px-2 py-1.5 align-top whitespace-pre-line break-words ${cell.className}`}
                        >
                          {cell.value || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
              <Button type="button" onClick={handleConfirmExport}>
                <Download className="size-4 mr-2" />
                Confirm Export
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
